import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { type } from "arktype";
import { X } from "lucide-react";

import { orpc } from "@/client";
import { useDebouncedSearch } from "@/hooks/use-debounced-search";
import { format } from "@/lib/format";
import { cn } from "@/lib/utils";

const patient = type({
	kind: type.enumerated("patient"),
	patientId: "number.integer > 0",
	sendpulseId: "string | null",
	name: "string > 0",
});
const lead = type({
	kind: type.enumerated("lead"),
	leadId: "number.integer > 0",
	sendpulseId: "string | null",
	name: "string > 0",
});

export const PersonSearchSchemas = { chosen: patient.or(lead) };

export type ChosenPerson = typeof PersonSearchSchemas.chosen.infer;

export function patientIdOf(chosen: ChosenPerson | null) {
	return chosen?.kind === "patient" ? chosen.patientId : null;
}

const MAX_RESULTS = 8;
const MIN_TERM = 2;

interface Suggestion {
	key: string;
	person: ChosenPerson;
	phone: string | null;
}

export function PersonSearch({
	chosen,
	onChoose,
	initialSearch = "",
	disabled = false,
	patientsOnly = false,
	placeholder = "Buscar paciente ou lead por nome",
	suggestionsPosition = "overlay",
}: {
	chosen: ChosenPerson | null;
	onChoose: (person: ChosenPerson | null) => void;
	initialSearch?: string;
	disabled?: boolean;
	patientsOnly?: boolean;
	placeholder?: string;
	suggestionsPosition?: "overlay" | "flow";
}) {
	const [search, setSearch] = useState(initialSearch);
	const [term, setTerm] = useState(initialSearch.trim());
	const [focused, setFocused] = useState(false);

	useDebouncedSearch(search, setTerm);

	const open = focused && term.length >= MIN_TERM;
	const active = chosen === null && open;

	const directory = useQuery({
		...orpc.people.directory.queryOptions({ input: { q: term, page: 1 } }),
		enabled: active,
	});

	if (chosen !== null) {
		return (
			<span className="status-badge w-fit border border-whatsapp/40 bg-whatsapp/10 text-whatsapp">
				{chosen.name}

				<button
					type="button"
					disabled={disabled}
					onClick={() => onChoose(null)}
					aria-label="Remover seleção"
					className="rounded-sm hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
				>
					<X className="size-3.5" />
				</button>
			</span>
		);
	}

	const suggestions: Suggestion[] = (directory.data?.items ?? [])
		.filter((person) => !patientsOnly || person.link === "patient_confirmed")
		.slice(0, MAX_RESULTS)
		.map((person) => {
			const name = format.readableName(person.name) ?? format.phone(person.phone) ?? person.name;

			if (person.link === "patient_confirmed") {
				return {
					key: `patient-${person.id}`,
					person: {
						kind: "patient",
						patientId: person.id,
						sendpulseId: person.sendpulse_id,
						name,
					},
					phone: format.phone(person.phone),
				};
			}

			return {
				key: `lead-${person.id}`,
				person: { kind: "lead", leadId: person.id, sendpulseId: person.sendpulse_id, name },
				phone: format.phone(person.phone),
			};
		});

	const loaded = directory.data !== undefined;

	return (
		<div
			className="relative"
			onFocus={() => setFocused(true)}
			onBlur={(event) => {
				if (!event.currentTarget.contains(event.relatedTarget)) {
					setFocused(false);
				}
			}}
		>
			<input
				type="text"
				data-slot="search-input"
				value={search}
				disabled={disabled}
				onChange={(event) => setSearch(event.target.value)}
				placeholder={placeholder}
				className="control-field h-11 px-2.5 md:h-10"
			/>

			{open && loaded && suggestions.length === 0 && (
				<p
					className={cn(
						"popover-panel z-20 mt-1 w-full px-2.5 py-1.5 text-xs italic text-muted-foreground",
						suggestionsPosition === "overlay" && "absolute left-0 top-full",
					)}
				>
					ninguém encontrado
				</p>
			)}

			{open && suggestions.length > 0 && (
				<ul
					className={cn(
						"popover-panel z-20 mt-1 w-full p-1",
						suggestionsPosition === "overlay" && "absolute left-0 top-full",
					)}
				>
					{suggestions.map((suggestion) => (
						<li key={suggestion.key}>
							<button
								type="button"
								data-slot="suggestion"
								data-key={suggestion.key}
								onClick={() => {
									onChoose(suggestion.person);
									setSearch("");
									setFocused(false);
								}}
								className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-xs font-medium text-foreground transition-colors hover:bg-secondary focus-visible:bg-secondary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
							>
								<span className="flex min-w-0 items-center gap-1.5">
									<span className="min-w-0 truncate">{suggestion.person.name}</span>

									{suggestion.person.kind === "lead" && (
										<span
											className={cn(
												"shrink-0 rounded-md border border-whatsapp/30 bg-whatsapp/10 px-1 py-px",
												"text-xs font-medium leading-none text-whatsapp",
											)}
										>
											lead
										</span>
									)}
								</span>

								<span className="shrink-0 font-mono text-xs text-muted-foreground">
									{suggestion.phone}
								</span>
							</button>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}
